import { useMutation, useQueryClient } from 'react-query';
import { PhoneMediaApi } from '@frontend/api-phone-media';
import { http } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useAlert } from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { logError } from '../../utils/loggers';

export type GenerateTTSParams = {
  name: string;
  message: string;
  language: string;
  voice: string;
  format?: string;
  input?: string;
  silence?: string;
};

const normalizeTTSParams = (params: GenerateTTSParams) => ({
  name: params.name,
  message: params.message,
  language: params.language,
  voice: params.voice,
  format: params.format ?? 'wav',
  input: params.input ?? 'ssml',
  silence: params.silence ?? '1000ms',
});

const BASE_URL = '/phone/audio-library/v1/tts';

const TTS_CACHE_KEY = 'tts-generate';

/**
 * A mutation-style hook for generating TTS audio with caching via React Query's queryClient.
 * Results are cached based on the input parameters, so repeated calls with the same
 * params will return cached results without making an HTTP request.
 */
export const useGenerateTTSMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: GenerateTTSParams) => {
      const normalizedParams = normalizeTTSParams(params);
      const cacheKey = [TTS_CACHE_KEY, normalizedParams];

      // Check if we have a cached result
      const cachedData = queryClient.getQueryData<Blob>(cacheKey);
      if (cachedData) {
        return cachedData;
      }

      const { name, message, language, voice, format, input, silence } = normalizedParams;

      const result = await http.post<Blob>(
        `${BASE_URL}/generate/${encodeURIComponent(name)}`,
        {
          text: message,
          format,
          lang: language,
          voice,
          input,
          silence,
        },
        { responseType: 'blob' }
      );

      // Store the result in query cache for future use
      queryClient.setQueryData(cacheKey, result);

      return result;
    },
  });
};

export const useAudioMutations = ({
  onSuccess,
  onClose,
  successMessage,
}: {
  onSuccess?: (uploadedId: string) => void;
  onClose?: () => void;
  successMessage?: string;
}) => {
  const { t } = useTranslation('phone');
  const alerts = useAlert();
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const tenantId = settingsTenantLocation?.phoneTenantId ?? '';
  const locationId = settingsTenantLocation?.id ?? '';
  const queryClient = useQueryClient();

  // Upload audio mutation
  const uploadMutation = useMutation(
    ({ file, fileName }: { file: File; fileName: string }) => {
      return PhoneMediaApi.uploadAudio(file, fileName, { tenantId });
    },
    {
      onSuccess: async (data) => {
        await new Promise<void>((res) =>
          setTimeout(() => {
            queryClient.invalidateQueries({
              predicate: (query) => {
                const key = queryKeys.audioPicker().at(-2) as string;
                return query.queryKey.includes(key);
              },
            });
            queryClient.invalidateQueries([tenantId, ...queryKeys.audioPicker()]);
            queryClient.invalidateQueries([locationId, ...queryKeys.phoneMedia()]);
            res();
            if (onSuccess && data?.audioFile.media_item_id) {
              onSuccess(data.audioFile.media_item_id);
            }
          }, 1000)
        );
        alerts.success(successMessage || t('Successfully uploaded audio file.'));
      },
      onError: (error) => {
        alerts.error(t('Failed to upload audio file.'));
        logError('useAudioMutations:uploadMutation | Failed to upload audio file', {
          data: { tenantId, locationId, error },
        });
      },
      onSettled: async () => {
        onClose?.();
      },
    }
  );

  // Generate TTS audio mutation
  const generateMutation = useMutation({
    mutationFn: ({
      name,
      message,
      language,
      voice,
      format = 'wav',
      input = 'ssml',
      silence = '1000ms',
    }: GenerateTTSParams) => {
      return http.post<Blob>(
        `${BASE_URL}/generate/${encodeURIComponent(name)}`,
        {
          text: message,
          format,
          lang: language,
          voice,
          input,
          silence,
        },
        { responseType: 'blob' }
      );
    },
  });

  return {
    uploadMutation,
    generateMutation,
  };
};
